import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { BodyDefault, Button, VirtualizedStack } from '@terra/ui-design-system';
import { Box, VStack } from '@terra/ui-styled-system';
import { ArrowDown01Icon, ArrowUp01Icon } from '@terra/ui-icons';
import type { PortalTarget } from '../../../store/projectStore';
import { LauncherNoMatches } from './LauncherNoMatches';
import { LauncherTargetRow } from './LauncherTargetRow';
import type { LauncherTargetItem } from './launcher-targets';
import type { LauncherTargetsStatus } from './useLauncherTargets';

const COLLAPSED_TARGET_COUNT = 4;

type LauncherTargetListProps = {
  status: LauncherTargetsStatus;
  items: LauncherTargetItem[];
  query: string;
  tenantName?: string;
  openTargetIds: ReadonlySet<string>;
  onOpen: (target: PortalTarget) => void;
  onSearchAnotherTenant?: () => void;
};

export function LauncherTargetList({
  status,
  items,
  query,
  tenantName,
  openTargetIds,
  onOpen,
  onSearchAnotherTenant,
}: LauncherTargetListProps) {
  const tSelector = useTranslations('proxy.applicationSelector');
  const tCommon = useTranslations('common');
  const [isExpanded, setIsExpanded] = useState(false);
  const hasQuery = query.trim().length > 0;

  const statusMessage = {
    preparing: tSelector('preparingWorkspace'),
    notReady: tSelector('workspaceNotReady'),
    loading: tCommon('loading'),
    error: tSelector('applicationsLoadError'),
    ready:
      items.length === 0 && !hasQuery ? tSelector('noApplicationsFound') : null,
  }[status];

  if (statusMessage) {
    return (
      <Box py="6" textAlign="center">
        <BodyDefault color="fg.neutral.muted">{statusMessage}</BodyDefault>
      </Box>
    );
  }

  if (items.length === 0) {
    return (
      <LauncherNoMatches
        query={query}
        tenantName={tenantName}
        onSearchAnotherTenant={onSearchAnotherTenant}
      />
    );
  }

  const hasMore = items.length > COLLAPSED_TARGET_COUNT;
  const showAll = isExpanded && hasMore;
  const renderRow = ({ item }: { item: LauncherTargetItem }) => (
    <LauncherTargetRow
      item={item}
      query={query}
      isOpen={openTargetIds.has(item.target.id)}
      onOpen={onOpen}
    />
  );

  return (
    <VStack gap="1" w="full" alignItems="stretch">
      {showAll ? (
        <Box h="360px">
          <VirtualizedStack
            data={items}
            computeItemKey={({ item }) => item.target.id}
            renderItem={renderRow}
          />
        </Box>
      ) : (
        items
          .slice(0, COLLAPSED_TARGET_COUNT)
          .map((item) => <Box key={item.target.id}>{renderRow({ item })}</Box>)
      )}
      {hasMore && (
        <Box>
          <Button
            variant="ghost"
            size="xs"
            color="fg.neutral.muted"
            onClick={() => setIsExpanded((expanded) => !expanded)}
          >
            {showAll ? tCommon('showLess') : tCommon('showMore')}
            {showAll ? (
              <ArrowUp01Icon size={14} />
            ) : (
              <ArrowDown01Icon size={14} />
            )}
          </Button>
        </Box>
      )}
    </VStack>
  );
}
